import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Stack,
} from "react-bootstrap";
import SuccessButton from "../../organisms/SuccessButton";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ListaClientes,
  NuevaPlantillaCorreoMasivo,
  NuevoCorreoMasivo as NuevoCorreoMasivoType,
  PERIODICIDADES_CORREOS_MASIVOS,
  PeriodicidadCorreoMasivo,
  QUERY_LEER_PLANTILLAS,
  crearPlantilla,
  enviarCorreo,
  labelPorPeriodicidadCorreoMasivo,
} from "../../../services/correosMasivosServicios";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { prepararCuerpoParaEnvio } from "./utils";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import {
  RespuestaLeerPropietarios,
  useLeerPropietarios,
} from "../../../services/propietariosServicios";
import PropietariosModal from "./PropietariosModal";
import { PropietarioIndividual } from "./PropietarioIndividual";

export const NuevoCorreoMasivo = ({
  listasClientes,
  onGuardar,
  onEnviar,
}: {
  listasClientes: ListaClientes[];
  onGuardar: (idPlantilla: string) => void;
  onEnviar: () => void;
}) => {
  const [cuerpo, setCuerpo] = useState<string | undefined>("");
  const [asunto, setAsunto] = useState<string | undefined>("");
  const [programar, setProgramar] = useState<boolean>(false);
  const [usarCuerpoGrafico, setUsarCuerpoGrafico] = useState<boolean>(false);
  const [usarGruposDePropietarios, setUsarGruposDePropietarios] =
    useState<boolean>(true);

  const [propietariosIndividuales, setPropietariosIndividuales] = useState<
    Array<RespuestaLeerPropietarios>
  >([]);

  const [grupoPropietarios, setGrupoPropietarios] = useState<
    string | undefined
  >(listasClientes[0]?.id);

  const [mostrarPropietariosModal, setMostrarPropietariosModal] =
    useState<boolean>(false);

  const [periodicidad, setPeriodicidad] = useState<
    PeriodicidadCorreoMasivo | undefined
  >(PERIODICIDADES_CORREOS_MASIVOS[0]);
  const [adjunto, setAdjunto] = useState<File | undefined>(undefined);
  const [cuerpoGrafico, setCuerpoGrafico] = useState<File | undefined>(
    undefined
  );

  const {
    data: propietarios,
    isError: propietariosIsError,
    isSuccess: propietariosIsSuccess,
  } = useLeerPropietarios();

  const propietariosPorSeleccionar = useMemo(() => {
    if (propietarios && propietariosIsSuccess) {
      if (propietariosIndividuales) {
        return propietarios.filter(
          (p) => !propietariosIndividuales.some((pi) => pi.id === p.id)
        );
      } else {
        return propietarios || [];
      }
    }
    return [];
  }, [propietarios, propietariosIndividuales, propietariosIsSuccess]);

  const queryClient = useQueryClient();

  const mutacionCrearPlantilla = useMutation(crearPlantilla, {
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_LEER_PLANTILLAS],
      });
      onGuardar(id);
    },
  });

  const mutacionEnviarCorreo = useMutation(enviarCorreo, {
    onSuccess: () => {
      onEnviar();
    },
  });

  const onCrearClick = () => {
    if (!!cuerpo && !!cuerpoGrafico) {
      throw Error("Cuerpo y cuerpo grafico no pueden estar ambos definidos");
    }
    const idsPropietariosIndividuales = propietariosIndividuales?.map(
      (p) => p.id
    );
    if (idsPropietariosIndividuales.length === 0 && !grupoPropietarios) {
      throw Error(
        "Grupo de propietarios y propietarios individuales no pueden ser nulos ambos"
      );
    }
    if (idsPropietariosIndividuales.length > 0 && grupoPropietarios) {
      throw Error(
        "Grupo de propietarios y propietarios individuales no pueden estar definidos ambos"
      );
    }
    const nuevaPlantilla: NuevaPlantillaCorreoMasivo = {
      propietariosIndividuales: idsPropietariosIndividuales,
      grupoPropietarios: grupoPropietarios,
      asunto: asunto!!,
      cuerpo: cuerpo ? prepararCuerpoParaEnvio(cuerpo!!) : undefined,
      cuerpoGrafico: cuerpoGrafico,
      periodicidad: programar ? periodicidad!! : undefined,
      adjunto: adjunto,
    };

    mutacionCrearPlantilla.mutate(nuevaPlantilla);
  };

  const onEnviarClick = () => {
    if (!!cuerpo && !!cuerpoGrafico) {
      throw Error("Cuerpo y cuerpo grafico no pueden estar ambos definidos");
    }
    const idsPropietariosIndividuales = propietariosIndividuales?.map(
      (p) => p.id
    );
    if (!idsPropietariosIndividuales && !grupoPropietarios) {
      throw Error(
        "Grupo de propietarios y propietarios individuales no pueden ser nulos ambos"
      );
    }
    if (idsPropietariosIndividuales && grupoPropietarios) {
      throw Error(
        "Grupo de propietarios y propietarios individuales no pueden estar definidos ambos"
      );
    }
    const nuevoCorreo: NuevoCorreoMasivoType = {
      propietariosIndividuales: idsPropietariosIndividuales,
      grupoPropietarios: grupoPropietarios,
      asunto: asunto!!,
      cuerpo: cuerpo ? prepararCuerpoParaEnvio(cuerpo!!) : undefined,
      cuerpoGrafico: cuerpoGrafico,
      adjunto: adjunto,
    };
    mutacionEnviarCorreo.mutate(nuevoCorreo);
  };

  const onAgregarPropietarioClick = () => {
    setMostrarPropietariosModal(true);
  };

  const onCerrarPropietariosModal = () => {
    setMostrarPropietariosModal(false);
  };

  const onSeleccionarPropietario = (propietario: RespuestaLeerPropietarios) => {
    if (!propietariosIndividuales) {
      setPropietariosIndividuales([propietario]);
    } else {
      setPropietariosIndividuales((p) => [...p!, propietario]);
    }
  };

  const onEliminarPropietario = (propietario: RespuestaLeerPropietarios) => {
    if (propietariosIndividuales) {
      setPropietariosIndividuales((px) =>
        px.filter((p) => p.id !== propietario.id)
      );
    }
  };

  const puedeGuardar =
    !!(grupoPropietarios || propietariosIndividuales) &&
    !!asunto &&
    (cuerpo || cuerpoGrafico) &&
    (programar ? !!periodicidad : true);

  if (mutacionEnviarCorreo.isError || propietariosIsError) {
    return <ServerErrorModal />;
  }

  return (
    <>
      {propietariosIsSuccess && (
        <PropietariosModal
          seleccionarPropietario={onSeleccionarPropietario}
          onCerrar={onCerrarPropietariosModal}
          propietarios={propietariosPorSeleccionar || []}
          mostrar={mostrarPropietariosModal}
        />
      )}
      <Card className="p-1">
        <Card.Body>
          <Stack gap={2}>
            <section>
              <h5>Redactar</h5>
            </section>
            <section>
              <Form.Check
                type="switch"
                id="usar-grupos-propietarios-switch"
                label="Usar grupos de propietarios"
                checked={usarGruposDePropietarios}
                onChange={() => {
                  setUsarGruposDePropietarios((p) => !p);
                  setGrupoPropietarios(undefined);
                  setPropietariosIndividuales([]);
                }}
              />
            </section>

            <section>
              {!usarGruposDePropietarios && (
                <div className="mb-3 row">
                  <div className="col col-auto">
                    <Button
                      size="sm"
                      id="boton-agregar-propietario"
                      onClick={onAgregarPropietarioClick}
                    >
                      Agregar propietario
                    </Button>
                  </div>

                  <div className="col col-auto d-flex flex-column gap-1">
                    {(propietariosIndividuales || []).map((p) => (
                      <PropietarioIndividual
                        propietario={p}
                        key={p.id}
                        onEliminar={onEliminarPropietario}
                      />
                    ))}
                  </div>
                </div>
              )}
              {usarGruposDePropietarios && (
                <InputGroup className="mb-3">
                  <InputGroup.Text id="para-label">Para:</InputGroup.Text>
                  <Form.Select
                    value={grupoPropietarios}
                    aria-label="para"
                    aria-describedby="para-label"
                    onChange={(v) => setGrupoPropietarios(v.target.value)}
                  >
                    {(listasClientes || []).map((cli) => (
                      <option key={cli.id} value={cli.id}>
                        {cli.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              )}

              <InputGroup className="mb-3">
                <InputGroup.Text id="asunto-label">Asunto:</InputGroup.Text>
                <Form.Control
                  aria-label="asunto"
                  aria-describedby="asunto-label"
                  value={asunto}
                  onChange={(a) => setAsunto(a.target.value)}
                />
              </InputGroup>
              <section>
                <Form.Check
                  type="switch"
                  id="cuerpo-grafico-switch"
                  label="Usar gráfico como cuerpo"
                  checked={usarCuerpoGrafico}
                  onChange={() => {
                    setUsarCuerpoGrafico((p) => !p);
                    setCuerpo(undefined);
                    setCuerpoGrafico(undefined);
                  }}
                />
              </section>
              {usarCuerpoGrafico && (
                <section className="mt-2">
                  <Form.Group>
                    <Form.Label>Cuerpo:</Form.Label>
                    <Form.Control
                      type="file"
                      aria-label="cuerpoGrafico"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const files = e.currentTarget.files;
                        if (files && files.length > 0) {
                          setCuerpoGrafico(files[0]);
                        } else {
                          setCuerpoGrafico(undefined);
                        }
                      }}
                    ></Form.Control>
                  </Form.Group>
                </section>
              )}
              {!usarCuerpoGrafico && (
                <section className="mt-2">
                  <ReactQuill
                    value={cuerpo}
                    onChange={(v) => setCuerpo(v)}
                    theme="snow"
                    modules={quillModules}
                  />
                </section>
              )}

              <section className="mt-2">
                <Form.Group>
                  <Form.Label>Adjunto:</Form.Label>
                  <Form.Control
                    type="file"
                    aria-label="adjunto"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const files = e.currentTarget.files;
                      if (files && files.length > 0) {
                        setAdjunto(files[0]);
                      } else {
                        setAdjunto(undefined);
                      }
                    }}
                  ></Form.Control>
                </Form.Group>
              </section>
              <section className="mt-3">
                <Form.Check
                  type="switch"
                  id="programar-switch"
                  label="Programar"
                  checked={programar}
                  onChange={() => {
                    setProgramar((p) => !p);
                  }}
                />
                {programar && (
                  <section className="mt-2 w-25">
                    <Form.Select
                      value={periodicidad}
                      aria-label="periodicidad"
                      aria-describedby="periodicidad-label"
                      onChange={(v) =>
                        setPeriodicidad(
                          v.target.value as PeriodicidadCorreoMasivo
                        )
                      }
                    >
                      {(PERIODICIDADES_CORREOS_MASIVOS || []).map((p) => (
                        <option key={p} value={p}>
                          {labelPorPeriodicidadCorreoMasivo[p]}
                        </option>
                      ))}
                    </Form.Select>
                  </section>
                )}
              </section>
            </section>
            <section className="d-flex gap-2 mt-3">
              <SuccessButton
                onClick={onEnviarClick}
                disabled={programar || !puedeGuardar}
                size="sm"
              >
                {mutacionEnviarCorreo.isLoading ||
                mutacionCrearPlantilla.isLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <span>Enviar</span> <SendIcon sx={{ fontSize: 18 }} />
                  </>
                )}
              </SuccessButton>
              <Button size="sm" disabled={!puedeGuardar} onClick={onCrearClick}>
                {mutacionEnviarCorreo.isLoading ||
                mutacionCrearPlantilla.isLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <span>Guardar</span> <SaveIcon sx={{ fontSize: 18 }} />
                  </>
                )}
              </Button>
            </section>
          </Stack>
        </Card.Body>
      </Card>
    </>
  );
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image"],
    ["clean"],
    [{ color: [] }, { background: [] }],
  ],
};
